import {
    SET_SELECTED_SELLER,
    SET_ALL_SELLERS,
    CLEAR_SELLERS_DATA,
    GET_CURRENT_USER_SELLERS_REQUEST_ERROR,
    SET_CURRENT_SELLER_STATISTIC,
    SET_CURRENT_SELLER_INFO,
    SET_CURRENT_SELLER_IS_LOADING,
    SET_CURRENT_SELLER_REQUEST_ERROR,
    SET_SELLER_REVIEWS_IFRAME_URL,
    SET_SELLER_AI_CLIENT_STATUS
} from "./constants";
import { ISellerData, ISellerStatistic } from "./types";
import sellerService from "api/services/sellerService";
import type { AppDispatch, RootState } from "redux/store";
import { setTemporaryNotifications } from "redux/notifications/actions";
import { defaultNotificationCreator } from "templates/Notifications";
import { standartError, informationRequestError, sellersRequestErrorMessage } from "constants/errors";
import { currentSellerId } from "constants/storageKeys";

export const setSelectedSellerData = (seller: ISellerData) => ({
    type: SET_SELECTED_SELLER,
    payload: seller
})

export const setUserSellers = (companies: ISellerData[]) => ({
    type: SET_ALL_SELLERS,
    payload: companies
})

export const clearSellersData = () => ({
    type: CLEAR_SELLERS_DATA,
})

export const getCurrentUserSellersRequestError = (error: string) => ({
    type: GET_CURRENT_USER_SELLERS_REQUEST_ERROR,
    payload: error
})

export const setCurrentSellerStatistic = (data: ISellerStatistic) => ({
    type: SET_CURRENT_SELLER_STATISTIC,
    payload: data
})

export const getCurrentUserSellers = ({ id }: { id: string }) => async (dispatch: AppDispatch) => {
    try {
        const response = await sellerService.list({
            per_page: 100
        });

        let sellers = []

        if (response && response.data && response.data?.data) {
            sellers = response.data.data.map((item: any, index: number) => {
                return {
                    sellerName: item.name,
                    id: item.id,
                    uuid: item.uuid,
                    userId: id
                };
            })

        }

        console.log("getCurrentUserSellers. Sellers count:", sellers.length)
        dispatch(setUserSellers(sellers))

        if (response.data.hasOwnProperty("error_backend_api_sellers")) {
            const { msg, status } = JSON.parse(response.data.error_backend_api_sellers.http_body);
            throw new Error(`${msg + " " + status}`)
        }
    } catch (error: any) {
        console.error(error.message);
        dispatch(getCurrentUserSellersRequestError(sellersRequestErrorMessage));
    }
}

export const getCurrentSellerStatistic = (sellerId: number) => async(dispatch: AppDispatch) => {
    try {
        const response = await sellerService.getCurrentSellerStatistic(sellerId);
        
        if(!response || response?.status > 299) {
            throw new Error(standartError)
        }

        if(response && response.data) {
            dispatch(setCurrentSellerStatistic(response.data.data))
        }
        
    } catch (error: any) {
        console.error(error.message);
        dispatch(setTemporaryNotifications(
            defaultNotificationCreator({
                title: informationRequestError,
                description: `${error.message}`,
                iconType: "warning"
            })
        ))
    }
}

export const getReviewIFrameUrl = (sellerId: number) => async(dispatch: AppDispatch, getState: () => RootState) => {
    try {
        const response = await sellerService.getReviewIFrameUrl(sellerId);
        if(!response || response?.status > 299) {
            throw new Error(standartError)
        }

        if(response && response.data && !response.data?.data?.url) {
            throw new Error(standartError)
        }

        if(response && response.data && response.data?.data?.url) {
            const currentState = getState();
            if (currentState.sellers.currentSeller.info.data?.aiIFrameUrl !== response.data?.data?.url) {
                dispatch(setSellerReviewIFrameUrl(response.data?.data?.url))
            }
        }

    } catch (error: any) {
        console.error(error.message);
        dispatch(setTemporaryNotifications(
            defaultNotificationCreator({
                title: informationRequestError,
                description: `${error.message}`,
                iconType: "warning"
            })
        ))
    }
}

export const getAndSetSellerById = (sellerId: number) => async(dispatch: AppDispatch, getState: () => RootState) => {
    try {
        dispatch(setCurrentSellerIsLoading(true));

        const response = await sellerService.getSellerById(sellerId) as any

        if(response.data.hasOwnProperty("error_backend_api_sellers")) {
            throw new Error(sellersRequestErrorMessage)
        }

        const aiResponse = await sellerService.isAiClientAvailable(sellerId) as any

        if(response.status <= 304 && response.data && response.data?.data) {
            dispatch(setCurrentSellerInfo({
                id: response.data.data.Id,
                sellerName: response.data.data.name,
                sla: response.data.data.sla,
                uuid: response.data.data.uuid,
                userId: getState().users.currentUser.data.id,
                isAiClient: aiResponse?.data?.success ?? false
            }))
            localStorage.setItem(currentSellerId, response.data.data.Id);
        } else {
            throw new Error(sellersRequestErrorMessage)
        }

    } catch (error: any) {
        console.error(error);
        const resolvedSeller = getState().sellers.allSellers[0]?.id;
        if(resolvedSeller) {
            window.location.pathname = `${process.env.REACT_APP_BASE_PATH}/${getState().sellers.allSellers[0].id}`
        } else {
            dispatch(setCurrentSellerRequestError(error.message));
        }
    } finally {
        dispatch(setCurrentSellerIsLoading(false));
    }
}

export const setCurrentSellerInfo = (data: ISellerData) => ({
    type: SET_CURRENT_SELLER_INFO,
    payload: data
})

export const setCurrentSellerIsLoading = (data: boolean) => ({
    type: SET_CURRENT_SELLER_IS_LOADING,
    payload: data
})

export const setCurrentSellerRequestError = (data: string) => ({
    type: SET_CURRENT_SELLER_REQUEST_ERROR,
    payload: data
})

export const setSellerReviewIFrameUrl = (data: string) => ({
    type: SET_SELLER_REVIEWS_IFRAME_URL,
    payload: data
})

export const setSellerAiClientStatus = (isAiClient: boolean) => ({
    type: SET_SELLER_AI_CLIENT_STATUS,
    payload: isAiClient
})

export const registerAiClient = (
    sellerId: number,
    core: string[]
) => async (dispatch: AppDispatch) => {
    const response = await sellerService.registerAiClient(sellerId, core);
    if (!response || response.status > 299) {
        throw new Error(standartError);
    }
    dispatch(setSellerAiClientStatus(true));
}